import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import api from '../utils/api';
import { getUser, logout } from '../utils/auth';
import { getCurrentSchool, getMainGuide, getRandomQuote, ASSETS, getCurrentUniverse, UNIVERSES, SUPER_HERO_CHARACTERS, GLOBE_TROTTER_CHARACTERS } from '../config/schools';
import { UniverseToggle } from '../components/UniverseSelector';
import AppHeader from '../components/AppHeader';
import LiveActivityNotifications from '../components/LiveActivityNotifications';
import { FaTrophy, FaFire, FaStar, FaNewspaper, FaPencilAlt, FaMicrophone, FaSearch, FaLightbulb, FaSignOutAlt, FaKeyboard, FaBook, FaChartLine, FaUserTie, FaExclamationCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

// Composant pour afficher les articles en attente avec badge clignotant
const PendingArticlesButton = ({ navigate }) => {
  const [pendingCount, setPendingCount] = useState(0);
  const [hasUrgent, setHasUrgent] = useState(false);

  useEffect(() => {
    const fetchPending = async () => {
      try {
        const response = await api.get('/articles/student/pending');
        setPendingCount(response.data.count || 0);
        setHasUrgent(response.data.pending?.some(a => a.isUrgent || a.isOverdue) || false);
      } catch (error) {
        // Silently fail if endpoint doesn't exist
      }
    };
    fetchPending();
    // Refresh every 5 minutes
    const interval = setInterval(fetchPending, 300000);
    return () => clearInterval(interval);
  }, []);

  if (pendingCount === 0) return null;

  return (
    <motion.div
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="mb-8"
    >
      <button
        onClick={() => navigate('/student/articles')}
        className={`w-full p-6 rounded-xl border-4 shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-4 text-white relative overflow-hidden ${
          hasUrgent 
            ? 'bg-gradient-to-r from-red-500 to-orange-600 border-red-700' 
            : 'bg-gradient-to-r from-blue-500 to-indigo-600 border-blue-700'
        }`}
        data-testid="pending-articles-button"
      >
        {/* Pulsing background for urgent */}
        {hasUrgent && (
          <motion.div
            animate={{ opacity: [0.3, 0.6, 0.3] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="absolute inset-0 bg-white"
          />
        )}
        
        <div className="relative flex items-center gap-4">
          <div className="relative">
            <FaPencilAlt className="text-4xl" />
            {/* Badge count */}
            <motion.span
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ repeat: Infinity, duration: 1 }}
              className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 text-black text-xs font-bold rounded-full flex items-center justify-center"
            >
              {pendingCount}
            </motion.span>
          </div>
          <div className="text-left">
            <h3 className="text-xl font-bold flex items-center gap-2">
              Articles à rédiger
              {hasUrgent && <FaExclamationCircle className="text-yellow-300 animate-pulse" />}
            </h3>
            <p className="text-sm opacity-90">
              {pendingCount} article{pendingCount > 1 ? 's' : ''} en attente
              {hasUrgent && ' - Deadline proche !'}
            </p>
          </div>
        </div>
      </button>
    </motion.div>
  );
};

function StudentDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const user = getUser();
  const navigate = useNavigate();
  const school = getCurrentSchool();
  const isEducatorDemo = user?.role === 'EDUCATOR_DEMO';

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/student/dashboard');
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F8F4E3' }}>
        <div className="onomatopoeia">LOADING...</div>
      </div>
    );
  }

  const strengths = dashboard?.skills?.strengths || [];
  const weaknesses = dashboard?.skills?.weaknesses || [];

  // Fonction pour obtenir le personnage approprié selon l'univers et le module
  const getCharacterForModule = (moduleType) => {
    const universe = getCurrentUniverse();
    
    if (universe === UNIVERSES.GLOBE_TROTTER) {
      // Globe-Trotter : personnages variés selon le module
      switch (moduleType) {
        case 'grammar':
          return GLOBE_TROTTER_CHARACTERS.archibald;
        case 'interview':
          return GLOBE_TROTTER_CHARACTERS.bianca;
        case 'seo':
          return GLOBE_TROTTER_CHARACTERS.seraphin;
        case 'factcheck':
          return GLOBE_TROTTER_CHARACTERS.lesPoliciers;
        case 'pitch':
          return GLOBE_TROTTER_CHARACTERS.nestor;
        default:
          return GLOBE_TROTTER_CHARACTERS.petitXXeme;
      }
    } else {
      // Super-Héros : personnages variés selon le module
      switch (moduleType) {
        case 'grammar':
          return SUPER_HERO_CHARACTERS.superRedChief;
        case 'interview':
          return SUPER_HERO_CHARACTERS.superInterviewer;
        case 'seo':
          return SUPER_HERO_CHARACTERS.superSeoGirl;
        case 'factcheck':
          return SUPER_HERO_CHARACTERS.superFactChecker;
        case 'pitch':
          return SUPER_HERO_CHARACTERS.superPitcher;
        default:
          return SUPER_HERO_CHARACTERS.main;
      }
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#F8F4E3' }}>
      {/* Header avec easter egg */}
      <AppHeader />

      <div className="container mx-auto px-4 py-8">
        {/* Stats Banner */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"
        >
          <div className="p-6 border-4 border-black shadow-[6px_6px_0px_rgba(0,0,0,0.3)] bg-gradient-to-br from-yellow-400 to-orange-500 text-center" data-testid="score-panel">
            <FaTrophy className="text-5xl mx-auto mb-2 text-white" />
            <h3 className="comic-title text-white text-4xl">{dashboard?.globalScore || 0}</h3>
            <p className="comic-text text-white text-xl">Score Global</p>
          </div>
          
          <div className="p-6 border-4 border-black shadow-[6px_6px_0px_rgba(0,0,0,0.3)] bg-gradient-to-br from-red-500 to-pink-600 text-center" data-testid="streak-panel">
            <FaFire className="text-5xl mx-auto mb-2 text-white" />
            <h3 className="comic-title text-white text-4xl">{dashboard?.streak || 0}</h3>
            <p className="comic-text text-white text-xl">Jours de suite</p>
          </div>
          
          <div className="p-6 border-4 border-black shadow-[6px_6px_0px_rgba(0,0,0,0.3)] bg-gradient-to-br from-blue-500 to-cyan-600 text-center">
            <FaStar className="text-5xl mx-auto mb-2 text-white" />
            <h3 className="comic-title text-white text-4xl">{dashboard?.recentAttempts?.length || 0}</h3>
            <p className="comic-text text-white text-xl">Exercices réalisés</p>
          </div>
        </motion.div>

        {/* Bouton Dashboard Éducateur (si mode démo enseignant) */}
        {isEducatorDemo && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="mb-8"
          >
            <button
              onClick={() => navigate('/educator-dashboard')}
              className="w-full p-6 bg-gradient-to-r from-green-600 to-emerald-700 rounded-xl border-4 border-green-800 shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-4 text-white"
              data-testid="educator-dashboard-button"
            >
              <FaUserTie className="text-4xl" />
              <div className="text-left">
                <h3 className="text-xl font-bold">Tableau de Suivi Élèves</h3>
                <p className="text-sm opacity-90">Gérer vos classes, suivre les performances, générer les bulletins</p>
              </div>
            </button>
            
            {/* Bouton Gestion Articles (pour professeurs) */}
            <button
              onClick={() => navigate('/professor/articles')}
              className="w-full mt-4 p-6 bg-gradient-to-r from-orange-500 to-amber-600 rounded-xl border-4 border-orange-700 shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-4 text-white"
              data-testid="professor-articles-button"
            >
              <FaPencilAlt className="text-4xl" />
              <div className="text-left">
                <h3 className="text-xl font-bold">Gestion des Articles</h3>
                <p className="text-sm opacity-90">Commander, corriger et publier les articles de vos élèves</p>
              </div>
            </button>
          </motion.div>
        )}

        {/* Bouton Articles à écrire (pour étudiants) */}
        {!isEducatorDemo && (
          <PendingArticlesButton navigate={navigate} />
        )}

        {/* Super Powers & Weaknesses */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <motion.div
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="comic-panel"
            data-testid="superpowers-panel"
          >
            <div className="flex items-center mb-4">
              <div className="onomatopoeia text-2xl mr-3">POW!</div>
              <h2 className="comic-title text-2xl" style={{ color: '#1B4965' }}>Tes Super Pouvoirs</h2>
            </div>
            {strengths.length > 0 ? (
              <ul className="space-y-2">
                {strengths.map((strength, idx) => (
                  <li key={idx} className="flex items-center space-x-2">
                    <FaStar className="text-yellow-500" />
                    <span className="comic-text text-lg">{strength}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="speech-bubble">
                <p className="text-gray-600">Continue à t'entraîner pour révéler tes super pouvoirs ! 💪</p>
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="comic-panel"
            data-testid="weaknesses-panel"
          >
            <div className="flex items-center mb-4">
              <div className="onomatopoeia text-2xl mr-3" style={{ color: '#E63946' }}>BOOM!</div>
              <h2 className="comic-title text-2xl" style={{ color: '#E63946' }}>À améliorer</h2>
            </div>
            {weaknesses.length > 0 ? (
              <ul className="space-y-2">
                {weaknesses.map((weakness, idx) => (
                  <li key={idx} className="flex items-center space-x-2">
                    <span className="text-2xl">⚡</span>
                    <span className="comic-text text-lg">{weakness}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="speech-bubble">
                <p className="text-gray-600">Aucune faiblesse détectée pour l'instant ! 🎯</p>
              </div>
            )}
          </motion.div>
        </div>

        {/* MISSIONS DU JOUR - Gros pavés colorés */}
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="comic-panel mb-8"
        >
          <h2 className="comic-title text-3xl mb-6 text-center" style={{ color: '#1B4965' }}>
            Missions du Jour
          </h2>
          
          {/* Grille de pavés colorés */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {/* Quiz Quotidien */}
            <motion.button
              whileHover={{ scale: 1.05, rotate: 1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/quiz-selection')}
              className="comic-panel p-4 cursor-pointer relative overflow-hidden"
              style={{ 
                background: dashboard?.todayQuizCompleted 
                  ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)' 
                  : 'linear-gradient(135deg, #1B4965 0%, #2D5A7B 100%)'
              }}
              data-testid="daily-quiz-button"
            >
              <FaNewspaper className="text-4xl mx-auto mb-2 text-white" />
              <h3 className="comic-text text-base text-white text-center">Quiz Quotidien</h3>
              {dashboard?.todayQuizCompleted ? (
                <div className="comic-badge bg-white text-green-600 mt-2">FAIT ✓</div>
              ) : (
                <div className="comic-badge bg-yellow-400 text-black mt-2">À FAIRE!</div>
              )}
            </motion.button>

            {/* Grammaire */}
            <motion.button
              whileHover={{ scale: 1.05, rotate: -1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/grammar')}
              className="comic-panel p-4 cursor-pointer relative"
              style={{ background: 'linear-gradient(135deg, #E63946 0%, #C1121F 100%)' }}
              data-testid="grammar-button"
            >
              {!dashboard?.visitedModules?.includes('grammar') && (
                <div className="absolute top-1 right-1 bg-yellow-400 text-black text-xs px-2 py-0.5 rounded-full font-bold animate-pulse">
                  NOUVEAU
                </div>
              )}
              <FaPencilAlt className="text-4xl mx-auto mb-2 text-white" />
              <h3 className="comic-text text-base text-white text-center">Grammaire</h3>
              <div className="comic-badge bg-white text-red-600 mt-2">ENTRAÎNE-TOI</div>
            </motion.button>

            {/* Interview */}
            <motion.button
              whileHover={{ scale: 1.05, rotate: 1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/interview')}
              className="comic-panel p-4 cursor-pointer relative"
              style={{ background: 'linear-gradient(135deg, #62B6CB 0%, #1B9AAA 100%)' }}
              data-testid="interview-button"
            >
              {!dashboard?.visitedModules?.includes('interview') && (
                <div className="absolute top-1 right-1 bg-yellow-400 text-black text-xs px-2 py-0.5 rounded-full font-bold animate-pulse">
                  NOUVEAU
                </div>
              )}
              <FaMicrophone className="text-4xl mx-auto mb-2 text-white" />
              <h3 className="comic-text text-base text-white text-center">Interview</h3>
              <div className="comic-badge bg-white text-cyan-600 mt-2">PRATIQUE!</div>
            </motion.button>

            {/* SEO */}
            <motion.button
              whileHover={{ scale: 1.05, rotate: -1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/seo')}
              className="comic-panel p-4 cursor-pointer relative"
              style={{ background: 'linear-gradient(135deg, #FFB703 0%, #FB8500 100%)' }}
              data-testid="seo-button"
            >
              {!dashboard?.visitedModules?.includes('seo') && (
                <div className="absolute top-1 right-1 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-bold animate-pulse">
                  NOUVEAU
                </div>
              )}
              <FaSearch className="text-4xl mx-auto mb-2 text-white" />
              <h3 className="comic-text text-base text-white text-center">SEO</h3>
              <div className="comic-badge bg-white text-orange-600 mt-2">APPRENDS!</div>
            </motion.button>

            {/* Pitch */}
            <motion.button
              whileHover={{ scale: 1.05, rotate: 1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/pitch')}
              className="comic-panel p-4 cursor-pointer relative"
              style={{ background: 'linear-gradient(135deg, #9B59B6 0%, #8E44AD 100%)' }}
              data-testid="pitch-button"
            >
              {!dashboard?.visitedModules?.includes('pitch') && (
                <div className="absolute top-1 right-1 bg-yellow-400 text-black text-xs px-2 py-0.5 rounded-full font-bold animate-pulse">
                  NOUVEAU
                </div>
              )}
              <FaLightbulb className="text-4xl mx-auto mb-2 text-white" />
              <h3 className="comic-text text-base text-white text-center">Pitch</h3>
              <div className="comic-badge bg-white text-purple-600 mt-2">PROPOSE!</div>
            </motion.button>

            {/* Histoire de la Presse */}
            <motion.button
              whileHover={{ scale: 1.05, rotate: -1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/press-history')}
              className="comic-panel p-4 cursor-pointer relative"
              style={{ background: 'linear-gradient(135deg, #8B4513 0%, #6B3410 100%)' }}
              data-testid="press-history-button"
            >
              {!dashboard?.visitedModules?.includes('press-history') && (
                <div className="absolute top-1 right-1 bg-yellow-400 text-black text-xs px-2 py-0.5 rounded-full font-bold animate-pulse">
                  NOUVEAU
                </div>
              )}
              <FaBook className="text-4xl mx-auto mb-2 text-white" />
              <h3 className="comic-text text-base text-white text-center">Histoire Presse</h3>
              <div className="comic-badge bg-white text-amber-700 mt-2">EXPLORE!</div>
            </motion.button>

            {/* Économie des Médias */}
            <motion.button
              whileHover={{ scale: 1.05, rotate: 1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/media-economics')}
              className="comic-panel p-4 cursor-pointer relative"
              style={{ background: 'linear-gradient(135deg, #2ECC71 0%, #27AE60 100%)' }}
              data-testid="media-economics-button"
            >
              {!dashboard?.visitedModules?.includes('media-economics') && (
                <div className="absolute top-1 right-1 bg-yellow-400 text-black text-xs px-2 py-0.5 rounded-full font-bold animate-pulse">
                  NOUVEAU
                </div>
              )}
              <FaChartLine className="text-4xl mx-auto mb-2 text-white" />
              <h3 className="comic-text text-base text-white text-center">Éco Médias</h3>
              <div className="comic-badge bg-white text-green-600 mt-2">COMPRENDS!</div>
            </motion.button>

            {/* Qui est Qui */}
            <motion.button
              whileHover={{ scale: 1.05, rotate: -1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/who-is-who')}
              className="comic-panel p-4 cursor-pointer relative"
              style={{ background: 'linear-gradient(135deg, #3498DB 0%, #2980B9 100%)' }}
              data-testid="who-is-who-button"
            >
              {!dashboard?.visitedModules?.includes('who-is-who') && (
                <div className="absolute top-1 right-1 bg-yellow-400 text-black text-xs px-2 py-0.5 rounded-full font-bold animate-pulse">
                  NOUVEAU
                </div>
              )}
              <FaUserTie className="text-4xl mx-auto mb-2 text-white" />
              <h3 className="comic-text text-base text-white text-center">Qui est Qui ?</h3>
              <div className="comic-badge bg-white text-blue-600 mt-2">TESTE-TOI!</div>
            </motion.button>

            {/* Style & Expressions */}
            <motion.button
              whileHover={{ scale: 1.05, rotate: 1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/style')}
              className="comic-panel p-4 cursor-pointer relative"
              style={{ background: 'linear-gradient(135deg, #9B59B6 0%, #8E44AD 100%)' }}
              data-testid="style-button"
            >
              {!dashboard?.visitedModules?.includes('style') && (
                <div className="absolute top-1 right-1 bg-yellow-400 text-black text-xs px-2 py-0.5 rounded-full font-bold animate-pulse">
                  NOUVEAU
                </div>
              )}
              <FaPencilAlt className="text-4xl mx-auto mb-2 text-white" />
              <h3 className="comic-text text-base text-white text-center">Style & Expr.</h3>
              <div className="comic-badge bg-white text-purple-600 mt-2">PERFECTIONNE!</div>
            </motion.button>

            {/* Typing Game */}
            <motion.button
              whileHover={{ scale: 1.05, rotate: 1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/typing')}
              className="comic-panel p-4 cursor-pointer relative"
              style={{ background: 'linear-gradient(135deg, #E74C3C 0%, #C0392B 100%)' }}
              data-testid="typing-button"
            >
              {!dashboard?.visitedModules?.includes('typing') && (
                <div className="absolute top-1 right-1 bg-yellow-400 text-black text-xs px-2 py-0.5 rounded-full font-bold animate-pulse">
                  NOUVEAU
                </div>
              )}
              <FaKeyboard className="text-4xl mx-auto mb-2 text-white" />
              <h3 className="comic-text text-base text-white text-center">Défense Mediapolis</h3>
              <p className="text-xs text-white/70 text-center">AZERTY</p>
              <div className="comic-badge bg-white text-red-600 mt-1">JOUE!</div>
            </motion.button>

            {/* Hall of Fame */}
            <motion.button
              whileHover={{ scale: 1.05, rotate: -1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/hall-of-fame')}
              className="comic-panel p-4 cursor-pointer relative overflow-hidden"
              style={{ background: 'linear-gradient(135deg, #1a0030 0%, #4a0080 100%)' }}
              data-testid="hall-of-fame-button"
            >
              <div className="absolute inset-0 opacity-30">
                <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,rgba(255,215,0,0.3),transparent_70%)]" />
              </div>
              <FaTrophy className="text-4xl mx-auto mb-2 text-yellow-400 relative z-10" style={{ filter: 'drop-shadow(0 0 10px rgba(255,215,0,0.8))' }} />
              <h3 className="comic-text text-base text-yellow-400 text-center relative z-10" style={{ textShadow: '0 0 10px rgba(255,215,0,0.5)' }}>Hall of Fame</h3>
              <p className="text-xs text-purple-300 text-center mt-1 relative z-10">Classement</p>
              <div className="comic-badge bg-yellow-400 text-purple-900 mt-1 relative z-10">TOP 10</div>
            </motion.button>
          </div>
        </motion.div>

        {/* Live Activity Panel - Only for Educator Demo */}
        {isEducatorDemo && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="comic-panel mt-8"
            style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)' }}
          >
            <LiveActivityNotifications maxNotifications={5} />
          </motion.div>
        )}

        {/* Guide Message - changes based on universe */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.6, type: 'spring' }}
          className="text-center"
        >
          {(() => {
            const guide = getMainGuide();
            return (
              <>
                <img 
                  src={guide.image}
                  alt={guide.name}
                  className="w-48 h-48 md:w-64 md:h-64 mx-auto object-contain mb-4"
                />
                <div className="speech-bubble max-w-2xl mx-auto">
                  <p className="comic-text text-lg md:text-xl">
                    "{getRandomQuote(guide)}"
                  </p>
                  <p className="text-sm text-gray-500 mt-2">— {guide.name}</p>
                </div>
              </>
            );
          })()}
        </motion.div>
      </div>
    </div>
  );
}

export default StudentDashboard;
